import React, { useCallback, useEffect, useMemo } from 'react';
import { JustificationSelect } from '~/components/Ticket/components/Filters/JustificationSelect/JustificationSelect';

import { Button, SelectDate, SelectSearch } from '~/ui/components';
import { Calendar } from '~/ui/assets/icons';
import { useAwaitControl } from 'react-redux-await-control';
import {
  getServiceChannels,
  getTicketCategories,
  getTicketJourneys,
  getTicketProducts,
  getTicketResolvingAreas,
} from '~/store/tickets/actions';
import { useDrawer } from '~/hooks/useDrawer';
import { StyledSelect } from '~/components/Ticket/components/Actions/Actions.styles';
import { ticketCreationOriginOptions } from '~/components/Ticket/common/ticketCreationOrigin';
import { TicketManagementPriorities } from '~/typings/enums/Ticket/TicketPriorities';
import RadioSelector from '~/ui/components/RadioSelector/RadioSelector';
import { normalizedInitialResolvingArea, normalizeProducts } from '~/common/tickets';
import { createSelectValues, findAreaById } from '~/common/selectValues';
import { StyledForm, Wrapper } from './AllFilters.styles';
import { StatusSelect } from '../StatusSelect/StatusSelect';
import { ResponsibleSelect } from '../ResponsibleSelect/ResponsibleSelect';
import { ReasonSelect } from '../ReasonSelect/ReasonSelect';
import { BankNameSelect } from '../BankNameSelect/BankNameSelect';
import { FinancialInstitutionSelect } from '../FinancialInstitutionSelect/FinancialInstitutionSelect';

type FiltersProps = {
  onChange: (value: any) => void;
  initialValues: any;
  onClear: () => void;
  hiddenFilters?: string[];
  drawerId?: string;
  availableLabels?: Record<string, string[]>;
};

export const SLA_OPTIONS = [
  { value: '1', label: '1 dia' },
  { value: '2', label: '2 dias' },
  { value: '3', label: '3 dias' },
  { value: '4', label: '4 dias' },
  { value: '>5', label: 'Maior que 5 dias' },
  { value: '>10', label: 'Maior que 10 dias' },
  { value: '>20', label: 'Maior que 20 dias' },
];

function normalizeLabel(s: string): string {
  return s.toLowerCase().trim().normalize('NFD').replace(/[̀-ͯ]/g, '');
}

function filterByLabels<T>(list: T[], labelKeys: keyof T | (keyof T)[], allowed?: string[]): T[] {
  if (!allowed?.length) return list;
  const allowedSet = new Set(allowed.map(normalizeLabel));
  const keys = Array.isArray(labelKeys) ? labelKeys : [labelKeys];
  return list.filter((item) => keys.some((key) => allowedSet.has(normalizeLabel(String(item[key] || '')))));
}

function withNoneOption<T>(list: T[], allowed: string[] | undefined, noneLabel: string, noneOption: T): T[] {
  if (!allowed?.length) return list;
  const target = normalizeLabel(noneLabel);
  const present = allowed.some((l) => normalizeLabel(l) === target);
  return present ? [noneOption, ...list] : list;
}

const NO_CATEGORY_OPTION = { id: -1, description: 'Sem categoria' };
const NO_PRODUCT_OPTION = { value: '__NONE__', label: 'Sem produto' };
const NO_JOURNEY_OPTION = { id: -1, name: 'Sem jornada' };

export function AllFilters({
  onChange,
  initialValues,
  onClear,
  hiddenFilters,
  drawerId = 'more-filters-drawer',
  availableLabels,
}: FiltersProps) {
  const { closeDrawer } = useDrawer();

  const getServiceChannelsControl = useAwaitControl(getServiceChannels);
  const getTicketResolvingAreasControl = useAwaitControl(getTicketResolvingAreas);
  const getTicketProductsControl = useAwaitControl(getTicketProducts);
  const getTicketCategoriesControl = useAwaitControl(getTicketCategories);
  const getTicketJourneysControl = useAwaitControl(getTicketJourneys);

  const isLoadingResolvingAreas = getTicketResolvingAreasControl.isRunning();
  const isLoadingChannels = getServiceChannelsControl.isRunning();
  const isLoadingProducts = getTicketProductsControl.isRunning();
  const categoriesLoading = getTicketCategoriesControl.isRunning();
  const journeysLoading = getTicketJourneysControl.isRunning();

  const serviceChannels = getServiceChannelsControl.result();
  const ticketResolvingAreas = getTicketResolvingAreasControl.result();
  const ticketProducts = getTicketProductsControl.result();
  const categories = getTicketCategoriesControl.result();
  const journeys = getTicketJourneysControl.result();

  const priorityOptions = Object.keys(TicketManagementPriorities).map((key) => ({
    value: key,
    label: TicketManagementPriorities[key],
  }));

  const initialJustification = useMemo(() => {
    if (!initialValues?.justificationIds) return [];

    return Array.isArray(initialValues?.justificationIds)
      ? initialValues?.justificationIds
      : [initialValues?.justificationIds];
  }, [initialValues]);

  const handleSubmit = useCallback(
    (values) => {
      function entryValues(value) {
        if (value && typeof value === 'object' && 'value' in value) {
          return (value as { value: any }).value;
        }

        if (Array.isArray(value) && !value.length) {
          return null;
        }

        if (typeof value === 'string' && !value.length) {
          return null;
        }

        return value;
      }

      const normalizedValues = Object.entries(values || {}).reduce(
        (acc, [key, value]) => ({
          ...acc,
          [key]: entryValues(value),
        }),
        {},
      );

      const payload: any = { ...normalizedValues };
      const rawValues = values || {};

      if ('resolvingareaId' in rawValues) {
        const rawAreaIds: number[] =
          Array.isArray(rawValues.resolvingareaId) && rawValues.resolvingareaId.length ? rawValues.resolvingareaId : [];
        const lastAreaId = rawAreaIds.at(-1);
        const lastAreaNode = lastAreaId != null ? findAreaById(ticketResolvingAreas, lastAreaId) : null;
        const isGroup = (lastAreaNode?.children?.length ?? 0) > 0;
        payload.resolvingareaId = isGroup ? null : lastAreaId ?? (normalizedValues as any).resolvingareaId;
        payload.groupResolvingAreaId = isGroup ? lastAreaId : null;
      }

      if ('channelId' in rawValues) {
        payload.channelId =
          Array.isArray(rawValues.channelId) && rawValues.channelId.length
            ? rawValues.channelId.at(-1)
            : (normalizedValues as any).channelId;
      }

      onChange?.(payload);
      closeDrawer(drawerId);
    },
    [ticketResolvingAreas],
  );

  const handleClear = useCallback(() => {
    closeDrawer(drawerId);
    onClear?.();
  }, []);

  const inputs = useMemo(
    () => [
      {
        id: 'product',
        label: 'Produto',
        initialValue: initialValues?.product ? { value: initialValues?.product } : null,
        input: (
          <SelectSearch
            placeholder="Produto"
            fullWidth
            searchable={false}
            options={withNoneOption(
              filterByLabels(normalizeProducts(ticketProducts) || [], ['label', 'value'], availableLabels?.product),
              availableLabels?.product,
              'Sem produto',
              NO_PRODUCT_OPTION,
            )}
            loading={isLoadingProducts}
          />
        ),
      },
      {
        id: 'date',
        label: 'Filtrar por data',
        initialValue: initialValues?.date ? initialValues.date : null,
        input: <SelectDate selectRange maxDate={new Date()} icon={<Calendar />} placeholder="Filtrar por data" />,
      },
      {
        id: 'priorityTicket',
        label: 'Prioridade',
        initialValue: initialValues?.priorityTicket ? initialValues?.priorityTicket : '',
        input: (
          <RadioSelector
            label="Prioridade"
            options={filterByLabels(priorityOptions, 'label', availableLabels?.priority)}
            orientation="row"
          />
        ),
      },
      {
        id: 'statusIds',
        label: 'Status',
        skipArrayFormat: true,
        initialValue: (() => {
          const raw = initialValues?.statusIds || initialValues?.statusId;
          if (raw == null) return [];
          return Array.isArray(raw) ? raw : [raw];
        })(),
        input: <StatusSelect multiple />,
      },
      {
        id: 'distributionFlag',
        label: 'Status',
        initialValue: initialValues?.distributionFlag != null ? [initialValues.distributionFlag] : null,
        input: (
          <StyledSelect
            options={[
              { value: true, label: 'Ativo' },
              { value: false, label: 'Inativo' },
            ]}
          />
        ),
      },
      {
        id: 'resolvingareaId',
        label: 'Área resolvedora',
        initialValue:
          normalizedInitialResolvingArea(
            ticketResolvingAreas,
            initialValues?.resolvingareaId ?? initialValues?.groupResolvingAreaId,
          ) ?? [],
        input: (
          <StyledSelect
            popupPlacement="left"
            placeholder="Área resolvedora"
            options={ticketResolvingAreas}
            loading={isLoadingResolvingAreas}
            changeOnSelect
            fieldNames={{ label: 'name', value: 'id' }}
          />
        ),
      },
      {
        id: 'categoryId',
        label: 'Categoria',
        initialValue: initialValues?.categoryId ? [initialValues?.categoryId] : null,
        input: (
          <StyledSelect
            options={withNoneOption(
              filterByLabels(categories || [], 'description', availableLabels?.category),
              availableLabels?.category,
              'Sem categoria',
              NO_CATEGORY_OPTION,
            )}
            loading={categoriesLoading}
            fieldNames={{ label: 'description', value: 'id' }}
            popupPlacement="left"
            placeholder="Categoria"
          />
        ),
      },
      {
        id: 'journeyId',
        label: 'Jornada',
        initialValue: initialValues?.journeyId ? [initialValues?.journeyId] : null,
        input: (
          <StyledSelect
            options={withNoneOption(
              filterByLabels(journeys || [], 'name', availableLabels?.journey),
              availableLabels?.journey,
              'Sem jornada',
              NO_JOURNEY_OPTION,
            )}
            loading={journeysLoading}
            fieldNames={{ label: 'name', value: 'id' }}
            popupPlacement="left"
            placeholder="Jornada"
          />
        ),
      },
      {
        id: 'sla',
        label: 'SLA',
        initialValue: initialValues?.sla ? [initialValues?.sla] : null,
        input: (
          <StyledSelect
            options={[
              { value: '1', label: '1 dia' },
              { value: '2', label: '2 dias' },
              { value: '3', label: '3 dias' },
              { value: '4', label: '4 dias' },
              { value: '>5', label: 'Maior que 5 dias' },
              { value: '>10', label: 'Maior que 10 dias' },
              { value: '>20', label: 'Maior que 20 dias' },
            ]}
          />
        ),
      },
      {
        id: 'responsibleId',
        label: 'Responsável',
        initialValue: initialValues?.responsibleId,
        input: <ResponsibleSelect />,
      },
      {
        id: 'reasonId',
        label: 'Motivo de abertura',
        initialValue: initialValues?.reasonId,
        input: <ReasonSelect allowedLabels={availableLabels?.reason} />,
      },
      {
        id: 'channelId',
        label: 'Canal',
        initialValue: createSelectValues(serviceChannels, initialValues?.channelId),
        input: (
          <StyledSelect
            placeholder="Canal"
            options={serviceChannels}
            loading={isLoadingChannels}
            fieldNames={{ label: 'name', value: 'id' }}
            popupPlacement="left"
          />
        ),
      },
      {
        id: 'creatorId',
        label: 'Criado por',
        initialValue: initialValues?.creatorId,
        input: <ResponsibleSelect />,
      },
      {
        id: 'creationOrigin',
        label: 'Origem de abertura',
        initialValue: initialValues?.creationOrigin ? [initialValues?.creationOrigin] : null,
        input: (
          <StyledSelect
            placeholder="Selecione a origem da abertura"
            popupPlacement="left"
            options={ticketCreationOriginOptions}
          />
        ),
      },
      {
        id: 'justificationIds',
        label: 'Justificativa da resolução',
        initialValue: initialJustification,
        input: <JustificationSelect />,
      },
      {
        id: 'financialInstitutionName',
        label: 'Fundo',
        initialValue: initialValues?.financialInstitutionName,
        input: <FinancialInstitutionSelect />,
      },
      {
        id: 'bankName',
        label: 'Consignatária',
        initialValue: initialValues?.bankName,
        input: <BankNameSelect />,
      },
    ],
    [
      serviceChannels,
      ticketProducts,
      categories,
      categoriesLoading,
      journeys,
      journeysLoading,
      initialValues,
      isLoadingResolvingAreas,
      isLoadingChannels,
      isLoadingProducts,
      initialJustification,
      availableLabels,
    ],
  );

  useEffect(() => {
    if (!serviceChannels.length) {
      getServiceChannelsControl.start();
    }

    if (!ticketResolvingAreas.length) {
      getTicketResolvingAreasControl.start();
    }

    if (!ticketProducts.length) {
      getTicketProductsControl.start();
    }

    if (!categories?.length) {
      getTicketCategoriesControl.start();
    }

    if (!journeys?.length) {
      getTicketJourneysControl.start();
    }
  }, []);

  const filteredInputs = useMemo(
    () => inputs.filter(({ id }) => !hiddenFilters?.includes(id)),
    [inputs, hiddenFilters],
  );

  return (
    <StyledForm name="reset-form" onSubmit={handleSubmit} inputs={filteredInputs}>
      <Wrapper>
        <Button rounded variant="outline" type="button" data-testid="ticket-form-clear-button" onClick={handleClear}>
          Limpar filtros
        </Button>
        <Button rounded type="submit" data-testid="ticket-form-button">
          Filtrar
        </Button>
      </Wrapper>
    </StyledForm>
  );
}
